import { useRef, useState } from 'react';
import Modal from './Modal';
import { Fld, Sel, TextA } from './Fields';
import { CATS, TRANS, FUELS, STATUSES, TX_LOCS } from '../lib/constants';

const EMPTY = {
  brand: '',
  model: '',
  yr: 2024,
  cat: 'Economy',
  seats: 5,
  doors: 4,
  bags: 2,
  trans: 'Automatic',
  fuel: 'Gas',
  mpg: 30,
  ppd: 49,
  dep: 200,
  status: 'Available',
  color: '#3B82F6',
  emoji: '🚗',
  img: '',
  vin: '',
  plate: '',
  odometer: 0,
  feats: '',
  locs: [],
  desc: '',
};

export default function VehicleForm({ initial, onSave, onClose }) {
  const [v, setV] = useState(initial ? { ...EMPTY, ...initial } : EMPTY);
  const [imgPrev, setImgPrev] = useState(v.img || '');
  const [imgErr, setImgErr] = useState(false);
  const [tab, setTab] = useState('basic');
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const upd = (k, val) => setV((p) => ({ ...p, [k]: val }));

  function handleFile(e) {
    const f = e.target.files[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = (ev) => {
      setImgPrev(ev.target.result);
      setImgErr(false);
      upd('img', ev.target.result);
    };
    r.readAsDataURL(f);
  }

  function toggleLoc(id) {
    const cur = v.locs || [];
    upd('locs', cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]);
  }

  const tabs = [
    ['basic', 'Vehicle Info'],
    ['specs', 'Specs & Pricing'],
    ['photos', 'Photos'],
    ['feats', 'Features'],
    ['locs', 'Locations'],
    ['tech', 'Technical'],
  ];

  async function handleSave() {
    if (!v.brand || !v.model) {
      alert('Brand and Model are required.');
      return;
    }
    setSaving(true);
    await onSave(v);
    setSaving(false);
  }

  return (
    <Modal title={initial?.id ? 'Edit Vehicle' : 'Add New Vehicle'} onClose={onClose} wide testId="vehicle-form-modal">
      <div style={{ display: 'flex', gap: 6, marginBottom: 20, flexWrap: 'wrap' }}>
        {tabs.map(([k, l]) => (
          <button
            key={k}
            data-testid={`vf-tab-${k}`}
            onClick={() => setTab(k)}
            style={{
              padding: '7px 14px',
              borderRadius: 8,
              border: 'none',
              background: tab === k ? '#0D1B3E' : '#F1F5F9',
              color: tab === k ? '#fff' : '#64748B',
              fontWeight: 700,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === 'basic' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Fld testId="vf-brand" label="Brand" value={v.brand} onChange={(e) => upd('brand', e.target.value)} required ph="Toyota" />
          <Fld testId="vf-model" label="Model" value={v.model} onChange={(e) => upd('model', e.target.value)} required ph="Camry" />
          <Fld testId="vf-year" label="Year" type="number" value={v.yr} onChange={(e) => upd('yr', +e.target.value)} min={2010} max={2026} />
          <Sel testId="vf-cat" label="Category" value={v.cat} onChange={(e) => upd('cat', e.target.value)} opts={CATS} />
          <Sel testId="vf-status" label="Status" value={v.status} onChange={(e) => upd('status', e.target.value)} opts={STATUSES} />
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 5 }}>
              Emoji Icon
            </label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {['🚗', '🚙', '🏎️', '🛻', '🚐', '⚡', '🚕'].map((em) => (
                <button
                  key={em}
                  onClick={() => upd('emoji', em)}
                  style={{
                    fontSize: 24,
                    padding: '6px 10px',
                    border: '2px solid',
                    borderColor: v.emoji === em ? '#E63946' : '#E2E8F0',
                    borderRadius: 8,
                    background: v.emoji === em ? '#FEE2E2' : '#fff',
                    cursor: 'pointer',
                  }}
                >
                  {em}
                </button>
              ))}
            </div>
          </div>
          <div style={{ gridColumn: '1/-1' }}>
            <TextA testId="vf-desc" label="Description" value={v.desc} onChange={(e) => upd('desc', e.target.value)} rows={2} />
          </div>
        </div>
      )}

      {tab === 'specs' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Fld label="Seats" type="number" value={v.seats} onChange={(e) => upd('seats', +e.target.value)} min={2} max={12} />
          <Fld label="Doors" type="number" value={v.doors || 4} onChange={(e) => upd('doors', +e.target.value)} min={2} max={5} />
          <Sel label="Transmission" value={v.trans} onChange={(e) => upd('trans', e.target.value)} opts={TRANS} />
          <Sel label="Fuel Type" value={v.fuel} onChange={(e) => upd('fuel', e.target.value)} opts={FUELS} />
          <Fld label="MPG (or MPGe)" type="number" value={v.mpg} onChange={(e) => upd('mpg', +e.target.value)} min={10} max={200} />
          <Fld label="Luggage Bags" type="number" value={v.bags || 2} onChange={(e) => upd('bags', +e.target.value)} min={0} max={10} />
          <Fld testId="vf-ppd" label="Price Per Day ($)" type="number" value={v.ppd} onChange={(e) => upd('ppd', +e.target.value)} min={1} required note="Daily rental rate before extras/tax" />
          <Fld label="Security Deposit ($)" type="number" value={v.dep} onChange={(e) => upd('dep', +e.target.value)} min={0} />
          <div>
            <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 5 }}>Accent Color</label>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
              {['#3B82F6', '#10B981', '#8B5CF6', '#F59E0B', '#EF4444', '#1D4ED8', '#374151', '#DC2626', '#6366F1'].map((c) => (
                <div
                  key={c}
                  onClick={() => upd('color', c)}
                  style={{
                    width: 28,
                    height: 28,
                    borderRadius: '50%',
                    background: c,
                    cursor: 'pointer',
                    border: '3px solid',
                    borderColor: v.color === c ? '#0D1B3E' : 'transparent',
                  }}
                />
              ))}
              <input type="color" value={v.color} onChange={(e) => upd('color', e.target.value)} style={{ width: 32, height: 32, border: 'none', padding: 0, cursor: 'pointer', borderRadius: 4 }} />
            </div>
          </div>
        </div>
      )}

      {tab === 'photos' && (
        <div>
          <div style={{ marginBottom: 16, textAlign: 'center' }}>
            {imgPrev && !imgErr ? (
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <img
                  src={imgPrev}
                  alt="Vehicle preview"
                  onError={() => setImgErr(true)}
                  style={{
                    width: '100%',
                    maxWidth: 360,
                    height: 200,
                    objectFit: 'contain',
                    borderRadius: 12,
                    border: '1.5px solid #E2E8F0',
                    background: '#F8FAFC',
                  }}
                />
                <button
                  onClick={() => {
                    setImgPrev('');
                    setImgErr(false);
                    upd('img', '');
                  }}
                  style={{ position: 'absolute', top: 8, right: 8, background: '#EF4444', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}
                >
                  ✕ Remove
                </button>
              </div>
            ) : (
              <div
                style={{
                  width: '100%',
                  maxWidth: 360,
                  height: 200,
                  background: `linear-gradient(135deg,${v.color}22,${v.color}44)`,
                  borderRadius: 12,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: '2px dashed #CBD5E1',
                  margin: '0 auto',
                }}
              >
                <span style={{ fontSize: 64 }}>{v.emoji}</span>
                <p style={{ color: '#94A3B8', fontSize: 12, marginTop: 8 }}>No photo — emoji shown as fallback</p>
              </div>
            )}
          </div>
          <button
            onClick={() => fileRef.current.click()}
            style={{ width: '100%', padding: '12px', border: '2px dashed #CBD5E1', borderRadius: 10, background: '#F8FAFC', color: '#0D1B3E', fontWeight: 700, fontSize: 13, cursor: 'pointer', marginBottom: 14 }}
          >
            📁 Upload Photo from Computer
          </button>
          <input ref={fileRef} type="file" accept="image/*" onChange={handleFile} style={{ display: 'none' }} />
          <Fld
            testId="vf-img-url"
            label="Or paste image URL"
            value={v.img && !v.img.startsWith('data:') ? v.img : ''}
            onChange={(e) => {
              upd('img', e.target.value);
              setImgPrev(e.target.value);
              setImgErr(false);
            }}
            ph="https://example.com/car.jpg"
            note="Supports JPG, PNG, WebP — emoji shows if URL fails"
          />
          <div style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: 12, fontSize: 12, color: '#166534' }}>
            💡 Photos are stored as base64 data (uploaded files) or URLs.
          </div>
        </div>
      )}

      {tab === 'feats' && (
        <div>
          <p style={{ fontSize: 13, color: '#64748B', marginBottom: 14 }}>Comma-separated features shown on the vehicle detail page.</p>
          <TextA label="Features (comma-separated)" value={v.feats} onChange={(e) => upd('feats', e.target.value)} rows={3} />
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 16 }}>
            {(v.feats || '')
              .split(',')
              .filter((f) => f.trim())
              .map((f, i) => (
                <span key={i} style={{ background: '#EFF6FF', color: '#1E40AF', borderRadius: 20, padding: '3px 12px', fontSize: 12, fontWeight: 600 }}>
                  {f.trim()}
                </span>
              ))}
          </div>
          <p style={{ fontSize: 12, color: '#94A3B8', marginBottom: 10 }}>Quick-add common features:</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {['Bluetooth', 'Navigation', 'Apple CarPlay', 'Android Auto', 'Backup Camera', 'Heated Seats', 'Sunroof', 'Wireless Charging', 'Keyless Entry', 'Remote Start', 'Lane Assist', 'Blind Spot Monitor', '360° Camera', 'Parking Assist', 'Premium Audio'].map((f) => (
              <button
                key={f}
                onClick={() => {
                  const cur = (v.feats || '')
                    .split(',')
                    .map((x) => x.trim())
                    .filter(Boolean);
                  if (!cur.includes(f)) upd('feats', [...cur, f].join(', '));
                }}
                style={{ padding: '4px 10px', borderRadius: 20, border: '1.5px solid #E2E8F0', background: '#fff', fontSize: 12, color: '#0F172A', fontWeight: 600, cursor: 'pointer' }}
              >
                + {f}
              </button>
            ))}
          </div>
        </div>
      )}

      {tab === 'locs' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14, alignItems: 'center', flexWrap: 'wrap' }}>
            <button data-testid="vf-loc-select-all" onClick={() => upd('locs', TX_LOCS.map((l) => l.id))} style={{ padding: '7px 14px', borderRadius: 8, border: '1.5px solid #E2E8F0', background: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              Select All
            </button>
            <button data-testid="vf-loc-clear" onClick={() => upd('locs', [])} style={{ padding: '7px 14px', borderRadius: 8, border: '1.5px solid #E2E8F0', background: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              Clear All
            </button>
            <span style={{ fontSize: 12, color: '#94A3B8' }}>
              {(v.locs || []).length} of {TX_LOCS.length} selected
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {TX_LOCS.map((loc) => {
              const checked = (v.locs || []).includes(loc.id);
              return (
                <label
                  key={loc.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '9px 12px',
                    borderRadius: 8,
                    border: '1.5px solid',
                    borderColor: checked ? '#0D1B3E' : '#E2E8F0',
                    background: checked ? '#EFF6FF' : '#fff',
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                >
                  <input type="checkbox" checked={checked} onChange={() => toggleLoc(loc.id)} style={{ accentColor: '#0D1B3E' }} />
                  <span>
                    {loc.name}
                    <span style={{ color: '#94A3B8', fontWeight: 500, marginLeft: 4 }}>· {loc.city}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {tab === 'tech' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Fld label="VIN Number" value={v.vin || ''} onChange={(e) => upd('vin', e.target.value)} ph="1HGBH41JXMN109186" note="17-character Vehicle ID Number" />
          <Fld label="License Plate" value={v.plate || ''} onChange={(e) => upd('plate', e.target.value)} ph="TX-ABC1234" />
          <Fld label="Current Odometer (miles)" type="number" value={v.odometer || 0} onChange={(e) => upd('odometer', +e.target.value)} min={0} />
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20, paddingTop: 16, borderTop: '1px solid #E2E8F0' }}>
        <button onClick={onClose} className="btn-ghost" data-testid="vf-cancel">
          Cancel
        </button>
        <button onClick={handleSave} className="btn-primary" disabled={saving} data-testid="vf-save">
          {saving ? 'Saving...' : initial?.id ? 'Save Changes' : 'Add Vehicle'}
        </button>
      </div>
    </Modal>
  );
}
